import React, { useEffect, useState } from "react";
import { adminApi } from "./api";
import { useAdminAuth } from "./AdminAuthContext";
import { Activity, KeyRound, Check, AlertCircle } from "lucide-react";

const TABS = [
  { value: "activity", label: "Activity", icon: Activity, roles: ["super_admin", "inventory_manager", "sales_manager"] },
  { value: "login", label: "Login history", icon: KeyRound, roles: ["super_admin"] },
];

export default function AdminLogs() {
  const { user } = useAdminAuth();
  const allowed = TABS.filter((t) => t.roles.includes(user.role));
  const [tab, setTab] = useState(allowed[0]?.value || "activity");
  const [data, setData] = useState({ activity: [], login: [] });

  useEffect(() => {
    adminApi.get("/logs/activity").then(({ data }) => setData((d) => ({ ...d, activity: data.logs })));
    if (user.role === "super_admin") {
      adminApi.get("/logs/login-history").then(({ data }) => setData((d) => ({ ...d, login: data.history })));
    }
  }, [user.role]);

  return (
    <div className="space-y-6" data-testid="admin-logs-page">
      <div>
        <p className="label-eyebrow text-gold">Audit trail</p>
        <h1 className="font-serif text-4xl text-bone mt-1">Activity Logs</h1>
      </div>

      <div className="flex gap-2">
        {allowed.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`label-eyebrow px-4 py-2.5 border transition-colors ${tab === t.value ? "border-gold text-gold" : "border-white/10 text-mist hover:border-gold hover:text-gold"}`}
            data-testid={`logs-tab-${t.value}`}
          >
            <t.icon className="w-3.5 h-3.5 inline mr-2" />{t.label}
          </button>
        ))}
      </div>

      {tab === "activity" && (
        <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="activity-logs-table">
          <table className="w-full text-sm">
            <thead className="bg-ink-950">
              <tr className="text-left label-eyebrow text-mist">
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Target</th>
              </tr>
            </thead>
            <tbody>
              {data.activity.map((l) => (
                <tr key={l.id} className="border-t border-white/5 text-bone/90 font-light">
                  <td className="px-4 py-3 text-mist text-xs">{new Date(l.ts).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">{l.user_email}</td>
                  <td className="px-4 py-3 label-eyebrow text-gold">{l.role}</td>
                  <td className="px-4 py-3 font-mono text-xs">{l.action}</td>
                  <td className="px-4 py-3 text-mist text-xs">{l.target || "—"}</td>
                </tr>
              ))}
              {data.activity.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-mist font-light italic">No activity yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "login" && (
        <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="login-history-table">
          <table className="w-full text-sm">
            <thead className="bg-ink-950">
              <tr className="text-left label-eyebrow text-mist">
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Outcome</th>
                <th className="px-4 py-3">User agent</th>
              </tr>
            </thead>
            <tbody>
              {data.login.map((l) => (
                <tr key={l.id} className="border-t border-white/5 text-bone/90 font-light">
                  <td className="px-4 py-3 text-mist text-xs">{new Date(l.ts).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">{l.email}</td>
                  <td className="px-4 py-3 font-mono text-xs text-mist">{l.ip}</td>
                  <td className={`px-4 py-3 label-eyebrow ${l.success ? "text-gold" : "text-rose-300"}`}>
                    {l.success ? <Check className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
                    {l.success ? "Success" : "Failed"}
                  </td>
                  <td className="px-4 py-3 text-mist text-xs truncate max-w-xs">{l.user_agent || "—"}</td>
                </tr>
              ))}
              {data.login.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-mist font-light italic">No login attempts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
